import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Loader2 } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import { useBrevoLists } from '../../queries/brevoLists';
import {
  EmailCampaign,
  useEmailCampaignRecipientCount,
  useEmailPreview,
  useSendEmailCampaign,
  useSendTestEmailCampaign,
  useUpdateEmailCampaign,
} from '../../queries/emailCampaigns';
import { HtmlCodeEditor } from '../../components/ui';
import { CampaignSendPanel } from './CampaignSendPanel';

interface EmailCampaignDetailDialogProps {
  campaign: EmailCampaign;
  onClose: () => void;
}

// n8n authors the draft, but the subject and HTML stay editable until the
// campaign is sent - the API has always allowed it (PUT /:id, which also
// clears testPassedAt so an edit forces a fresh test send); this dialog
// shipped read-only by mistake. Sent campaigns remain view-only.
export function EmailCampaignDetailDialog({ campaign, onClose }: EmailCampaignDetailDialogProps) {
  const isSent = campaign.status === 'sent';
  const [subject, setSubject] = useState(campaign.subject_line);
  const [html, setHtml] = useState(campaign.html_body);
  // Opens on the rendered email - that's what you want to look at first.
  // "Edit HTML" switches to the source.
  const [showPreview, setShowPreview] = useState(true);
  const [selectedListIds, setSelectedListIds] = useState<number[]>(campaign.brevo_list_ids ?? []);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [sendTestError, setSendTestError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  const { data: lists = [] } = useBrevoLists();
  const { data: preview } = useEmailPreview(subject, html, showPreview);
  const update = useUpdateEmailCampaign();
  const sendTest = useSendTestEmailCampaign();
  const send = useSendEmailCampaign();
  const { data: recipientCount } = useEmailCampaignRecipientCount(campaign.id, selectedListIds);

  const isDirty = subject !== campaign.subject_line || html !== campaign.html_body;

  const handleSave = () => {
    setSaveError(null);
    update.mutate(
      { id: campaign.id, subject_line: subject, html_body: html },
      { onError: (err) => setSaveError(extractErrorMessage(err, 'Save failed.')) },
    );
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-background p-6 shadow-lg max-h-[90vh] overflow-y-auto">
          <Dialog.Title className="text-lg font-semibold">{campaign.campaign_id}</Dialog.Title>
          <p className="mt-1 text-sm text-muted-foreground">{campaign.email_type} — {campaign.tips_date}</p>

          <div className="mt-4">
            <label htmlFor="ec-subject" className="mb-1 block text-sm font-medium">Subject</label>
            <input
              id="ec-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={isSent}
              className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
            />
          </div>

          <div className="mt-3">
            <div className="mb-1 flex items-center justify-between">
              {showPreview || isSent ? (
                <span className="block text-sm font-medium">Email</span>
              ) : (
                <label htmlFor="ec-html" className="block text-sm font-medium">HTML body</label>
              )}
              {!isSent && html.trim() && (
                <button
                  type="button"
                  onClick={() => setShowPreview((v) => !v)}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  {showPreview ? 'Edit HTML' : 'Preview email'}
                </button>
              )}
            </div>
            {showPreview || isSent ? (
              <>
                <p className="mb-1 text-xs text-muted-foreground">Shown with the branded header and footer recipients receive.</p>
                {/* Sandboxed (no scripts) so the email's own CSS can't touch the admin. */}
                {/* The raw body renders immediately and is replaced by the
                    branded version when it arrives - blanking it while the
                    request was in flight is what left an empty white box.
                    Chrome does not reliably re-navigate a srcdoc document when
                    the attribute is mutated, so that swap remounts the element
                    (hence the key). allow-same-origin rather than sandbox="" so
                    it renders; with no allow-scripts nothing in the email runs. */}
                <iframe
                  key={preview?.html ? 'branded' : 'raw'}
                  title="preview"
                  srcDoc={preview?.html ?? html}
                  sandbox="allow-same-origin"
                  className="h-64 w-full rounded-md border border-input bg-white"
                />
              </>
            ) : (
              <HtmlCodeEditor id="ec-html" value={html} onChange={setHtml} height="16rem" />
            )}
          </div>

          {saveError && <p className="mt-2 text-sm text-destructive">{saveError}</p>}
          {!isSent && (
            <div className="mt-3 flex items-center justify-end gap-2">
              {isDirty && <p className="mr-auto text-xs text-muted-foreground">Unsaved edits — save before sending.</p>}
              <button
                type="button"
                onClick={handleSave}
                disabled={update.isPending || !isDirty || !subject.trim() || !html.trim()}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                {update.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Save
              </button>
            </div>
          )}

          <div className="mt-4">
            <CampaignSendPanel
              status={campaign.status}
              testPassedAt={campaign.metadata?.testPassedAt}
              // A test send is encouraged but optional for tips emails - the
              // "Send to Audience" button must not stay disabled when no test
              // has run (or after one has). Matches the marketing-campaign flow.
              testRequired={false}
              // Unsaved edits would otherwise be sent as the old content: the
              // send path reads html_body from the database, not this form.
              blockedReason={isDirty ? 'Save your edits before sending.' : undefined}
              listOptions={lists}
              selectedListIds={selectedListIds}
              onSelectedListIdsChange={setSelectedListIds}
              recipientCount={recipientCount?.count}
              onSendTest={(testEmails) => {
                setSendTestError(null);
                sendTest.mutate(
                  { id: campaign.id, testEmails, listIds: selectedListIds },
                  { onError: (err) => setSendTestError(extractErrorMessage(err, 'Test send failed.')) },
                );
              }}
              sendTestPending={sendTest.isPending}
              sendTestError={sendTestError}
              onSend={(overrideSent) => {
                setSendError(null);
                send.mutate(
                  { id: campaign.id, listIds: selectedListIds, overrideSent },
                  { onError: (err) => setSendError(extractErrorMessage(err, 'Send failed.')) },
                );
              }}
              sendPending={send.isPending}
              sendError={sendError}
            />
          </div>

          <div className="mt-4 flex justify-end">
            <button type="button" onClick={onClose} className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent">
              Close
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
